import {useEffect} from "react";

import {useGroupSession} from "~/contexts/session/GroupSessionProvider";

interface HostGameStarterParams {
  levelId: string | undefined; // The level ID from the current route
}

/**
 * A hook that should only be used by a host.
 * It automatically starts a new game round when the host navigates to a new level page.
 * It compares the current level ID from the route with the level ID in the
 * active game session's `current_round`.
 */
export const useHostGameStarter = ({levelId}: HostGameStarterParams) => {
  const {isHost, sessionData, startNewGameRound} = useGroupSession();

  useEffect(() => {
    // --- Guard Clauses ---
    // 1. Only the host can start a game.
    // 2. We must have a valid levelId from the URL.
    // 3. We must have active session data.
    if (!isHost || !levelId || !sessionData) {
      return;
    }

    const currentRoundLevelId = sessionData.current_round?.levelId;

    // --- CRITICAL FIX: PREVENT INFINITE LOOP ---
    // Only start a new round if there is no current round, OR if the current round's
    // level ID does not match the level ID of the current page.
    if (currentRoundLevelId !== levelId) {
      console.log(
        `HostGameStarter: Triggering new round. Reason: Current level '${currentRoundLevelId}' !== Target level '${levelId}'`,
      );

      startNewGameRound(levelId).catch((error: any) => {
        console.error("Failed to auto-start new round:", error);
      });
    }

    // This dependency array is correct. The effect needs to re-evaluate the condition
    // whenever the page (levelId) or session data changes. The logic *inside* the
    // effect now prevents the infinite loop.
  }, [levelId, isHost, sessionData, startNewGameRound]);
};
